// @flow
import React, { useState, useEffect, useRef, useCallback } from 'react'
import cx from 'classnames'
import isBoolean from 'lodash/isBoolean'
import { Autocomplete, Popper, styled, TextField } from '@mui/material'
import type { Option, InputProps } from 'app/core/types'
import FontIcon from 'app/components/FontIcon/FontIcon.jsx'
import { getColorFromBackground } from 'app/libs/helpers/getColorFromBackground'
import { colors } from 'app/styles/colors.js'
import LoaderSmall from 'app/components/LoaderSmall/LoaderSmall.jsx'
import { cyLabelFormater } from 'app/libs/helpers/cyTools.js'
import { assetIcons } from 'app/components/Icons/assetsIcons'
import tagsClasses from 'app/components/Table/Cells/CellAssetsSelect/CellAssetsSelect.module.scss'

import Validators from '../Validators/Validators.jsx'

let timer: TimeoutID | void

export type AutocompleteSelectProps = {|
  autoComplete?: boolean, // If true, the portion of the selected suggestion that has not been typed by the user, known as the completion string, appears inline after the input cursor in the textbox. The inline completion string is visually highlighted and has a selected state.
  autoHighlight?: boolean,
  autoSelect?: boolean, // If true, the selected option becomes the value of the input when the Autocomplete loses focus unless the user chooses a different option or changes the character string in the input.
  blurOnSelect?: 'mouse' | 'touch' | boolean,
  ChipProps?: Object, // Props applied to the Chip element.
  classes?: Object, // Override or extend the styles applied to the component. See CSS API below for more details.
  clearOnBlur?: boolean, // !props.freeSolo	If true, the input's text will be cleared on blur if no value is selected.
  clearOnEscape?: boolean, // false	If true, clear all values when the user presses escape and the popup is closed.
  clearOnFocus?: boolean,
  clearOnValidate?: boolean,
  clearText?: string, // 'Clear'	Override the default text for the clear icon button.
  closeIcon?: React$Element<any> | null, // <CloseIcon fontSize="small" />	The icon to display in place of the default close icon.
  closeText?: string, // 'Close'	Override the default text for the close popup icon button.
  debug?: boolean, // false	If true, the popup will ignore the blur event if the input is filled. You can inspect the popup markup with your browser tools. Consider this option when you need to customize the component.
  defaultValue?: any, // props.multiple ? [] : null	The default input value. Use when the component is not controlled.
  disableClearable?: boolean, // false	If true, the input can't be cleared.
  disableCloseOnSelect?: boolean, // false	If true, the popup won't close when a value is selected.
  disabled?: boolean, // false	If true, the input will be disabled.
  disabledItemsFocusable?: boolean, // false	If true, will allow focus on disabled items.
  disableListWrap?: boolean, // false	If true, the list box in the popup will not wrap focus.
  disablePortal?: boolean, // false	Disable the portal behavior. The children stay within it's parent DOM hierarchy.
  filterOptions?: (options: Array<Option>, state: Object) => void, // A filter function that determines the options that are eligible.
  filterSelectedOptions?: boolean, // false	If true, hide the selected options from the list box.
  forcePopupIcon?: 'auto' | boolean, // 'auto'	Force the visibility display of the popup icon.
  freeSolo?: boolean, // false	If true, the Autocomplete is free solo, meaning that the user input is not bound to provided options.
  fullWidth?: boolean, // false	If true, the input will take up the full width of its container.
  getLimitTagsText?: (more: number) => React$Element<any> | string, // 	(more) => +${more}	The label to display when the tags are truncated (limitTags).
  getOptionDisabled?: (option: Option) => boolean, // Used to determine the disabled state for a given option.
  getOptionLabel?: (option: Option) => string, // (x) => x	Used to determine the string value for a given option. It's used to fill the input (and the list box options if renderOption is not provided).
  getOptionSelected?: (option: Option, value: any) => boolean, // 		Used to determine if an option is selected, considering the current value. Uses strict equality by default.
  groupBy?: (options: Option) => string, // If provided, the options will be grouped under the returned string. The groupBy value is also used as the text for group headings when renderGroup is not provided.
  handleHomeEndKeys?: boolean, // !props.freeSolo	If true, the component handles the "Home" and "End" keys when the popup is open. It should move focus to the first option and last option, respectively.
  id?: string, // This prop is used to help implement the accessibility logic. If you don't provide this prop. It falls back to a randomly generated id.
  includeInputInList?: boolean, // false	If true, the highlight can move to the input.
  inputValue?: string, // The input value.
  limitTags?: number, // -1	The maximum number of tags that will be visible when not focused. Set -1 to disable the limit.
  ListboxComponent?: (params: Object) => React$Element<any> | string, // 'ul'	The component used to render the listbox.
  ListboxProps?: Object, // Props applied to the Listbox element.
  loading?: boolean, // false	If true, the component is in a loading state.
  loadingText?: React$Element<any>, // 'Loading…'	Text to display when in a loading state.
  multiple?: boolean, // false	If true, value must be an array and the menu will support multiple selections.
  noOptionsText?: React$Element<any>, // 'No options'	Text to display when there are no options.
  onClose?: (event: Object, reason: string) => void, // Callback fired when the popup requests to be closed. Use in controlled mode (see open).
  onHighlightChange?: (event: Object, option: Option, reason: string) => void, // Callback fired when the highlight option changes.
  onInputChange?: (event: Object, value: string, reason: string) => void, // Callback fired when the input value changes.
  onOpen?: (event: Object) => void, // Callback fired when the popup requests to be opened. Use in controlled mode (see open).
  open?: boolean, // Control the popup` open state.
  openOnFocus?: boolean, // false	If true, the popup will open on input focus.
  openText?: string, // 'Open'	Override the default text for the open popup icon button.
  PaperComponent?: (params: Object) => React$Element<any> | string, // Paper	The component used to render the body of the popup.
  PopperComponent?: (params: Object) => React$Element<any> | string, // Popper	The component used to position the popup.
  popupIcon?: React$Element<any>, // <ArrowDropDownIcon />	The icon to display in place of the default popup icon.
  renderGroup?: (params: Object) => React$Element<any>, // Render the group.
  renderInput?: (params: Object) => React$Element<any>, // Render the input.
  renderOption?: (params: Object) => React$Element<any>, // Render the option, use getOptionLabel by default.
  renderTags?: (options: Array<Option>, getTagProps: Function) => React$Element<any> | Array<React$Element<any>>, // Render the selected value.
  selectOnFocus?: boolean, // !props.freeSolo	If true, the input's text will be selected on focus. It helps the user clear the selected value.
  size?: 'medium' | 'small', // 'medium'	The size of the autocomplete.
  options?: Array<Option>,
  value?: any,
  label?: string,

  // ADDED PROPS
  ...InputProps,
  onChange?: (option: any) => any, // Callback fired when the value changes.
  disabledOptions?: Array<string>,
  inputRef?: React$ElementRef<any>,
  onSearch?: (inputValue: string) => Promise<any>,
  autoFocus?: boolean,
  additionnalOptions?: Array<Option>,
  defaultOpenOptions?: boolean,
  inputProps?: Object,
  renderLabel?: (opt: Option) => React$Element<any>,
  dataCy?: string | React$Node,
  customItemLabel?: (option: Option) => React$Node,
  subLabel?: React$Node,
  startAdornment?: React$Node,
  endAdornment?: React$Node,
  noStyle?: boolean,
  titled?: boolean,

  // Validators
  validatorRef?: Function,
  validatorStyle?: Object,
  errors?: Object,
  isRequired?: boolean,
  validators?: Object,
|}

const MUIAutocomplete = styled((props) => <Autocomplete {...props} />)(({ multiple, titled, focus }) => ({
  '&.MuiAutocomplete-root': {
    width: '100%',
    borderRadius: '4px',
    padding: '0 !important',
  },
  '& .MuiOutlinedInput-root': {
    padding: '0 8px',
  },
  '& .MuiAutocomplete-option': {
    margin: 0,
    padding: 0,
    minWidth: 180,
    '&:hover': { boxShadow: '0 0 0 30px rgba(0,0,0,0.05) inset' },
  },
  '& .MuiAutocomplete-listbox': {
    padding: '0px !important',
  },
  '& .MuiAutocomplete-input': {
    border: 'none',
    color: '#4a4a4a',
    padding: '4px 0px !important',
    fontSize: titled && !focus ? '18px' : '16px',
    fontWeight: titled && !focus ? 'bold' : 'normal',
  },
  '& .MuiAutocomplete-paper': {
    borderRadius: '3px',
    padding: 0,
    margin: 0,
  },
  '& .MuiAutocomplete-groupUl': {
    padding: 0,
  },
  '& .MuiAutocomplete-inputFocused': {
    fontSize: '16px',
    fontWeight: 'normal',
  },
  '& .MuiAutocomplete-inputRoot': {
    border: 'none !important',
    '&:focus': {
      border: '1px solid red',
    },
    '&:not(:focus-within)': {
      flexWrap: titled && multiple ? 'nowrap !important' : undefined,
    },
  },
}))

const MUITextfield = styled((props) => <TextField {...props} />)(({ multiple, titled, noStyle }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.MuiTextField-root': {
    borderRadius: noStyle ? undefined : 4,
    height: '100%',
    padding: '0',
    '&:focus-within': {
      border: `1px solid ${noStyle ? 'transparent' : '#B3B9c7'}`,
      backgroundColor: '#ffffff',
    },
  },
}))

export function MUIAutocompleteSelect(props: AutocompleteSelectProps): React$Node {
  const {
    disabledOptions,
    options: _options,
    onChange,
    onBlur,
    onClick,
    onFocus,
    inputRef,
    openOnFocus,
    defaultOpenOptions,
    onSearch,
    closeIcon,
    open,
    multiple,
    placeholder,
    autoFocus,
    validatorRef,
    validatorStyle,
    errors,
    clearOnEscape = true,
    isRequired = false,
    validators,
    value,
    additionnalOptions,
    inputProps: unusedVar,
    disabled,
    renderLabel,
    defaultValue,
    clearOnValidate,
    clearOnFocus,
    loading,
    dataCy,
    renderTags,
    customItemLabel,
    startAdornment: _startAdornment,
    endAdornment: _endAdornment,
    subLabel,
    renderOption,
    noStyle: _noStyle,
    label,
    titled,
    getLimitTagsText,
    limitTags,
    ...rest
  } = props

  const [options, setOptions] = useState(onSearch && typeof value === 'object' ? [value] : [])
  const [filteredOptions, setFilteredOptions] = useState([])
  const [searchOptions, setSearchOptions] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedValue, setSelectedValue] = useState(value)
  const [searchingString, setSearchingString] = useState('')
  const [startAdornment, setStartAdornment] = useState()
  const [focus, setFocus] = useState(false)
  const [error, setError] = useState(false)

  const isMounted = useRef()
  const _inputRef = useRef()

  const getLabelFromValue = useCallback(
    (value: void | null | Option) => {
      if (Array.isArray(value)) return value?.map(getLabelFromValue) || []
      if (typeof value === 'string' && _options)
        return options.concat(_options).find((opt) => opt?.value === value)?.label || value || ''
      if (typeof value === 'object') return value?.label || ''
      return ''
    },
    [options, _options],
  )

  const getValueFromValue = useCallback(
    (value: void | null | Option) => {
      if (Array.isArray(value)) return value?.map(getValueFromValue) || []
      if (typeof value === 'string' && _options)
        return options.concat(_options).find((opt) => opt?.value === value)?.value || value || null
      if (typeof value === 'object') return value?.value || null
      return null
    },
    [options, _options],
  )

  function getIcon(option) {
    if (multiple) return
    if (option?.icon) setStartAdornment(option.icon)
    else if (option?.assetType && typeof option.assetType === 'string') setStartAdornment(assetIcons(option.assetType))
    else if (option?.data?.icon) setStartAdornment(option.data.icon)
    else if (option?.data?.assetType) setStartAdornment(assetIcons(option.data.assetType))
  }

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    setSelectedValue(value)
    setSearchingString(getLabelFromValue(value))
  }, [value])

  useEffect(() => {
    getIcon(selectedValue)
  }, [selectedValue])

  useEffect(() => {
    let sortedOptions = _options || []

    if (additionnalOptions) sortedOptions = sortedOptions.concat(additionnalOptions)
    if (searchOptions?.length > 0) sortedOptions = sortedOptions.concat(searchOptions)
    if (onSearch && !multiple && typeof value === 'object') {
      sortedOptions = [...sortedOptions.filter((opt) => opt.value !== value?.value), value]
    }

    setOptions(sortedOptions)
  }, [_options, searchOptions, disabledOptions, additionnalOptions])

  useEffect(() => {
    let newFilteredOptions = options
    if (!onSearch) {
      if (disabledOptions) newFilteredOptions = newFilteredOptions.filter((opt) => disabledOptions.includes(opt.value))
      if (searchingString) newFilteredOptions = newFilteredOptions.filter((opt) => opt.label.includes(searchingString))
      setFilteredOptions(newFilteredOptions)
    } else {
      setFilteredOptions(newFilteredOptions)
    }
  }, [options, disabledOptions, searchingString])

  useEffect(() => {
    if (loading) setIsSearching(true)
    return () => {
      setIsSearching(false)
    }
  }, [loading])

  const noStyle = _noStyle || (titled && !focus && value)
  const showEndAdornment = (titled && focus) || !titled || !value || !noStyle

  function _onChange(event: SyntheticInputEvent<any>, result: Object) {
    if (Array.isArray(result) && onChange) {
      onChange(
        result?.map((val) => {
          if (typeof val === 'string')
            return options.find((opt) => opt.value === val) || value?.find((opt) => opt.value === val) || val
          return val
        }) || [],
      )
    } else if (typeof result === 'object' && onChange) {
      getIcon(result)
      onChange(result)
    }

    setSearchOptions([])

    if (clearOnValidate) setSelectedValue()
    else setSelectedValue(result)

    if (!multiple) setFocus(false)

    setFilteredOptions(options)
  }

  function _onInputChange(event: SyntheticInputEvent<>, inputText: string) {
    if (!inputText && isSearching) return
    if (startAdornment) setStartAdornment()
    setSearchingString(inputText)

    if (onSearch && (!inputText || inputText.length === 0)) {
      if (isMounted.current) setSearchOptions([])
      return
    }

    if (onSearch && inputText && inputText.length > 0) {
      if (isMounted.current) setIsSearching(true)

      clearTimeout(timer)
      timer = setTimeout(() => {
        onSearch(inputText).then((results) => {
          if (isMounted.current) {
            setSearchOptions(results)
            setIsSearching(false)
          }
        })
      }, 500)
    }
  }

  function _renderOption(optionProps, option: Option, state) {
    const { selected } = state || {}
    const { label, backgroundColor, icon, value, labelCustom } = option
    const { style, ...restOptionProps } = optionProps
    if (labelCustom) {
      return (
        <div
          className="flex row noWrap alignCenter fullWidth"
          style={{
            ...style,
            backgroundColor: backgroundColor || selected ? 'rgba(0,0,0,0.05)' : undefined,
            color: backgroundColor && getColorFromBackground(backgroundColor),
          }}
          data-testid={cyLabelFormater('autocomplete-choice', value || customItemLabel?.(option) || label)}
          {...restOptionProps}
          key={value}
        >
          {labelCustom}
        </div>
      )
    }

    return (
      <div
        className="flex row noWrap alignCenter fullWidth"
        style={{
          ...style,
          backgroundColor: backgroundColor || selected ? 'rgba(0,0,0,0.05)' : undefined,
          color: backgroundColor && getColorFromBackground(backgroundColor),
        }}
        data-testid={cyLabelFormater('autocomplete-choice', value || customItemLabel?.(option) || label)}
        {...restOptionProps}
        key={value}
      >
        {icon ? <FontIcon icon={icon} className="marginRight5" /> : null}
        {customItemLabel?.(option) || label}
        {multiple && selected ? <FontIcon icon="fas-check" className="marginLeftAuto fontSize12" /> : null}
      </div>
    )
  }

  function _renderTags(items: Array<string>, getTagProps: Function): Array<React$Element<any>> {
    return items.map((optionValue: string, index: number) => {
      const option =
        value?.find((opt) => opt.value === optionValue) || options.find((opt) => opt.value === optionValue) || {}
      const tagProps = getTagProps({ index })
      const { onDelete, className, ...rest } = tagProps
      const { label, data, icon } = option

      return (
        <div {...rest} key={optionValue} className={`${tagsClasses.item} ${className}`}>
          {icon || data?.assetType ? (
            <FontIcon icon={icon || assetIcons(data?.assetType)} className="marginRight5" />
          ) : null}{' '}
          <div className="ellipsis">{label}</div>
          <FontIcon icon="close" style={{ padding: '0 0 0 4px', cursor: 'pointer' }} onClick={onDelete} />
        </div>
      )
    })
  }

  function getInputRef(ref: React$ElementRef<any>) {
    if (autoFocus && !_inputRef.current) {
      ref?.select?.()
      _inputRef.current = ref
    }

    if (inputRef) {
      if (typeof inputRef === 'function') inputRef(ref)
      else if (typeof inputRef === 'object') inputRef.current = ref
    }
  }

  function _onClick(event: SyntheticMouseEvent<>) {
    event.stopPropagation()
    if (onClick) onClick(event)
    setFocus(false)
    setSearchingString(getLabelFromValue(selectedValue))
  }

  function _renderInput(InputParams: Object) {
    const { InputProps, inputProps, ...inputParams } = InputParams

    return (
      <MUITextfield
        InputProps={{
          ...InputProps,
          startAdornment:
            startAdornment || _startAdornment || InputProps.startAdornment ? (
              <>
                {_startAdornment}
                {startAdornment ? <FontIcon icon={startAdornment} className="grey marginRight5" /> : null}
                {InputProps.startAdornment}
              </>
            ) : undefined,
          endAdornment:
            _endAdornment ||
            (isSearching ? (
              <LoaderSmall />
            ) : showEndAdornment ? (
              <FontIcon
                icon="fas-search"
                onClick={_onClick}
                className={focus ? undefined : 'grey'}
                style={{ cursor: 'pointer' }}
              />
            ) : undefined),
          inputProps: {
            ...inputProps,
            'data-testid': cyLabelFormater('input', dataCy || placeholder),
            value: multiple ? inputProps.value : searchingString,
          },
          style: { marginTop: 0 },
        }}
        label={label}
        {...inputParams}
        onClick={(event: SyntheticMouseEvent<>) => {
          setFocus(true)
        }}
        inputRef={getInputRef}
        placeholder={placeholder}
      />
    )
  }

  function _getOptionSelected(option: Option, selectedValue: ?mixed) {
    if (!selectedValue) return true
    if (!option) return false

    if (typeof selectedValue === 'string' && selectedValue === '') {
      return false
    }
    if (typeof selectedValue === 'object') {
      return option.value === selectedValue
    }
    if (['string', 'number'].includes(typeof selectedValue)) {
      return option.value === selectedValue
    }

    return false
  }

  function _onBlur(event: SyntheticFocusEvent<>) {
    if (onBlur) onBlur(event)
    setFocus(false)
    setSearchingString(getLabelFromValue(selectedValue))
  }

  function _onFocus(event: SyntheticFocusEvent<>) {
    if (onFocus) onFocus(event)
    setFocus(true)
    setFilteredOptions(options)
  }

  return (
    <div className="relative fullWidth">
      <MUIAutocomplete
        autoComplete={true}
        freeSolo={multiple}
        forcePopupIcon={false}
        clearOnEscape={clearOnEscape}
        disableClearable={true}
        autoSelect={false}
        clearOnBlur={false}
        openOnFocus={openOnFocus || defaultOpenOptions}
        renderInput={_renderInput}
        filterSelectedOptions={!!onSearch && !multiple}
        filterOptions={multiple ? undefined : () => filteredOptions}
        fullWidth={true}
        data-testid="autocomplete"
        PopperComponent={(popperProps: Object) => {
          const { style, ...popperPropsRest } = popperProps
          return <Popper style={{ ...style, width: undefined, margin: '5px 0 5px 0' }} {...popperPropsRest} />
        }}
        noOptionsText="No results"
        limitTags={limitTags || (titled && multiple ? 0 : undefined)}
        getLimitTagsText={
          getLimitTagsText ||
          (titled && multiple && value
            ? (number) => (
                <div className="ellipsis bold">
                  {/* $FlowFixMe */}
                  {value?.map((proj) => proj?.label || proj).join(', ')}
                </div>
              )
            : undefined)
        }
        onChange={_onChange}
        open={disabled ? false : isBoolean(open) ? open : focus && options.length > 0}
        disabled={disabled}
        loading={isSearching}
        onFocus={_onFocus}
        onBlur={_onBlur}
        onClick={_onClick}
        renderTags={renderTags || _renderTags}
        onInputChange={_onInputChange}
        {...rest}
        options={options}
        renderOption={renderOption || _renderOption}
        getOptionLabel={(option: Option) => {
          return option.label || options.find((opt) => opt.value === option)?.label || ''
        }}
        isOptionEqualToValue={_getOptionSelected}
        value={getValueFromValue(selectedValue)}
        multiple={multiple}
      />
      {subLabel}
      <Validators
        ref={(ref) => {
          if (validatorRef) validatorRef(ref)
        }}
        style={{ ...validatorStyle, position: 'absolute', right: 0 }}
        value={value || ''}
        errors={errors}
        onSuccess={() => setError(false)}
        onError={() => setError(true)}
        validators={{
          required: isRequired,
          ...validators,
        }}
      />
    </div>
  )
}
